import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText, {
	ListItemTextProps,
} from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import * as React from 'react';
import { ILinkUrl } from '../actions/testForm';
import * as Actions from '../actions/testResult';
import i18n from '../resources/i18n';
import FormControl from '../utils/FormControlWrapper';
import { getLinkUrl } from '../utils/ReplaceLinkUrl';
export interface ITestResultQuestionProps {
	question: Actions.IQuestion;
	classes: {
		question: string;
		questionImage: string;
		questionImageContainer: string;
		questionTitle: string;
		resultRight: string;
		resultWrong: string;
		hint: string;
	};
	linkUrlParam: ILinkUrl;
}

const selectAnswersComponent: (
	kind: Actions.QuestionKind
) => React.ComponentType<ITestResultQuestionProps> = (
	kind: Actions.QuestionKind
) => {
	switch (kind) {
		case Actions.QuestionKind.CHECK:
			return CheckAnswers;
		case Actions.QuestionKind.RADIO:
			return RadioAnswers;
		case Actions.QuestionKind.SELECT:
			return SelectAnswers;
		case Actions.QuestionKind.TEXT:
			return TextAnswers;
		default:
			const exhaustiveCheck: never = kind;
			throw Error(exhaustiveCheck);
	}
};

const internationalizeQuestionScore = (original: string) => {
	const m1 = /^([^\s\d]+)(\d+)([^\s\d]+)$/.exec(original);
	return m1
		? i18n.t(m1[1], { ns: 'force' }) +
				m1[2] +
				i18n.t(m1[3], { ns: 'force' })
		: original;
};

const Header = ({
	classes,
	question,
	linkUrlParam,
}: ITestResultQuestionProps) => (
	<Grid container={true} spacing={3}>
		<Grid style={{ flex: 1 }} xs={true}>
			<Typography variant="h6" className={classes.questionTitle}>
				<span>{question.questionNo}</span>
				<br />
				{getLinkUrl(linkUrlParam, question.questionText)
					.split('\r\n')
					.map((item, i) => (
						<React.Fragment key={i}>
							<span
								dangerouslySetInnerHTML={{
									__html: item,
								}}
							/>
							<br />
						</React.Fragment>
					))}
			</Typography>
		</Grid>
		<Grid>
			<Typography>
				{internationalizeQuestionScore(question.questionScore)}
			</Typography>
		</Grid>
	</Grid>
);

const QuestionImage: React.FC<ITestResultQuestionProps> = ({
	question,
	classes,
}: ITestResultQuestionProps) =>
	question.imageFilePath ? (
		<div className={classes.questionImageContainer}>
			<img
				src={question.imageFilePath}
				className={classes.questionImage}
			/>
		</div>
	) : null;

const internationalizeAnswerScore = (original: string) => {
	const m2 = /^([^\s\d]+)(\d+)([^\s\d]+)$/.exec(original);
	return m2
		? i18n.t(m2[1], { ns: 'force' }) +
				m2[2] +
				i18n.t(m2[3], { ns: 'force' })
		: original;
};

const AnswerScore: React.SFC<ITestResultQuestionProps> = ({
	question,
}: ITestResultQuestionProps) => (
	<Typography variant="subtitle1" align="right">
		{internationalizeAnswerScore(question.answerScore)}
	</Typography>
);

const Hints: React.FC<ITestResultQuestionProps> = ({
	question,
	classes,
	linkUrlParam,
}: ITestResultQuestionProps) =>
	question.hint || question.hintFilePath ? (
		<div className={classes.hint}>
			<Typography variant="h6">
				{i18n.t('TestResultQuestion_0001')}
			</Typography>
			{question.hint ? (
				<Typography>
					{getLinkUrl(linkUrlParam, question.hint)
						.split('\r\n')
						.map((item, i) => (
							<React.Fragment key={i}>
								<span
									dangerouslySetInnerHTML={{
										__html: item,
									}}
								/>
								<br />
							</React.Fragment>
						))}
				</Typography>
			) : null}
			{question.hintFilePath ? (
				<div className={classes.questionImageContainer}>
					<img
						src={question.hintFilePath}
						className={classes.questionImage}
					/>
				</div>
			) : null}
		</div>
	) : null;

/**
 * 問題部分のコンポーネント
 * @param props
 */
const TestResultQuestion: React.FC<ITestResultQuestionProps> = props => {
	const { classes, question } = props;
	const Answers = selectAnswersComponent(question.questionKind);

	return (
		<div className={classes.question}>
			<Header {...props} />
			<QuestionImage {...props} />
			<Answers {...props} />
			<AnswerScore {...props} />
			<Hints {...props} />
		</div>
	);
};
export default TestResultQuestion;

/**
 * radio形式の回答部分
 * @param props
 */
const RadioAnswers = (props: ITestResultQuestionProps) => {
	const { question, classes } = props;
	const answers = question.answers as Actions.IRadioAnswer[];
	return (
		<React.Fragment>
			{answers.map((answer, i) => (
				<Grid
					key={i}
					container={true}
					spacing={2}
					alignItems="flex-start"
				>
					<Grid item={true} style={{ minWidth: '42px' }}>
						<Typography>{answer.displayNum}</Typography>
					</Grid>
					<Grid item={true} xs={true}>
						<List>
							{answer.options.map((option, j) => {
								const p: ListItemTextProps = {
									primary:
										i18n.t(option.answerText, {
											ns: 'force',
										}) +
										' ' +
										i18n.t(option.answerMessage, {
											ns: 'force',
										}),
								};
								if (option.answerFlg === 1) {
									p.primaryTypographyProps = {
										className: classes.resultRight,
									};
								} else if (option.answerFlg === 2) {
									p.primaryTypographyProps = {
										className: classes.resultWrong,
									};
								}
								return (
									<ListItem key={j}>
										<ListItemText {...p} />
									</ListItem>
								);
							})}
						</List>
					</Grid>
				</Grid>
			))}
		</React.Fragment>
	);
};

/**
 * text形式の回答部分
 * @param props
 */
const TextAnswers = (props: ITestResultQuestionProps) => {
	const { question, classes } = props;
	const answers = question.answers as Actions.ITextAnswer[];
	return (
		<List>
			{answers.map((answer, i) => {
				const p: ListItemTextProps = {
					primary:
						answer.answerText +
						' ' +
						i18n.t(answer.answerMessage, { ns: 'force' }),
				};
				if (answer.answerFlg === 1) {
					p.primaryTypographyProps = {
						className: classes.resultRight,
					};
				} else if (answer.answerFlg === 2) {
					p.primaryTypographyProps = {
						className: classes.resultWrong,
					};
				}
				return (
					<ListItem key={i}>
						<div style={{ minWidth: '27px' }}>
							<Typography>{answer.displayNum}</Typography>
						</div>
						<ListItemText {...p} />
					</ListItem>
				);
			})}
		</List>
	);
};

/**
 * check形式の回答部分
 * @param props
 */
const CheckAnswers = (props: ITestResultQuestionProps) => {
	const { question, classes } = props;
	const answers = question.answers as Actions.ICheckAnswer[];
	let messageClass: string | undefined;
	if (question.answerResultType === 1) {
		messageClass = classes.resultRight;
	} else if (question.answerResultType === 2) {
		messageClass = classes.resultWrong;
	}
	const paddingLeft: React.CSSProperties = {
		paddingLeft: '67px',
	};
	const fieldsetWidth: React.CSSProperties = {
		display: 'flex',
	};
	return (
		<React.Fragment>
			<FormControl component="fieldset" style={fieldsetWidth}>
				<FormGroup>
					{answers.map((answer, i) => (
						<FormControlLabel
							key={i}
							control={
								<Checkbox
									checked={answer.selected}
									disabled={true}
								/>
							}
							label={answer.answerText}
							style={paddingLeft}
						/>
					))}
				</FormGroup>
			</FormControl>
			<Typography className={messageClass} style={paddingLeft}>
				{i18n.t(question.answerResultMessage || '', { ns: 'force' })}
			</Typography>
		</React.Fragment>
	);
};

/**
 * select形式の回答部分
 * @param props
 */
const SelectAnswers = (props: ITestResultQuestionProps) => {
	const { question, classes } = props;
	const answers = question.answers as Actions.ISelectAnswer[];
	return (
		<List>
			{answers.map(answer =>
				answer.options.map((option, i) => {
					// 実際にはひとつしかこない
					const p: ListItemTextProps = {
						primary:
							i18n.t(option.answerText, { ns: 'force' }) +
							' ' +
							i18n.t(option.answerMessage, { ns: 'force' }),
					};
					if (option.answerFlg === 1) {
						p.primaryTypographyProps = {
							className: classes.resultRight,
						};
					} else if (option.answerFlg === 2) {
						p.primaryTypographyProps = {
							className: classes.resultWrong,
						};
					}
					return (
						<ListItem key={i}>
							<div style={{ minWidth: '26px' }}>
								<Typography>{answer.displayNum}</Typography>
							</div>
							<ListItemText {...p} />
						</ListItem>
					);
				})
			)}
		</List>
	);
};



// WEBPACK FOOTER //
// ./src/components/TestResultQuestion.tsx